import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { 
  Plus, 
  Search, 
  Filter, 
  Users, 
  Trophy, 
  Clock, 
  DollarSign,
  BookOpen,
  Target,
  TrendingUp,
  Bell,
  Settings,
  RefreshCw,
  Eye,
  Play,
  Pause,
  CheckCircle,
  AlertCircle,
  Star
} from 'lucide-react';
import { 
  GroupCompetition, 
  GroupCompetitionForm, 
  GroupSearchFilters,
  GroupCompetitionStats 
} from '../../types/group-competition.types';
import GroupCompetitionCreator from './GroupCompetitionCreator';
import GroupCompetitionLobby from './GroupCompetitionLobby';
import GroupCompetitionResults from './GroupCompetitionResults';

interface GroupCompetitionDashboardProps {
  currentUserId: string;
  onClose: () => void;
}

// Animations
const slideIn = keyframes`
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

// Styled Components
const DashboardOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%);
  z-index: 1000;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  min-height: 90vh;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: white;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 20px;
`;

const TitleIcon = styled.span`
  font-size: 3rem;
  animation: ${float} 3s ease-in-out infinite;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
`;

const CreateButton = styled.button`
  background: linear-gradient(45deg, #ffd700, #ffed4e);
  color: #1e293b;
  border: none;
  padding: 15px 30px;
  border-radius: 25px;
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 10px;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 30px rgba(255, 215, 0, 0.4);
  }
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 12px 20px;
  border-radius: 25px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  font-size: 14px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }
`;

const ContentArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 30px;
`;

const StatsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 25px;
  text-align: center;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: rgba(255, 255, 255, 0.2);
    background: rgba(255, 255, 255, 0.08);
    transform: translateY(-5px);
  }
`;

const StatIcon = styled.div<{ color: string }>`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${props => props.color};
  margin: 0 auto 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const StatValue = styled.div`
  font-size: 2.5rem;
  font-weight: 800;
  color: white;
  margin-bottom: 10px;
`;

const StatLabel = styled.div`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 600;
`;

const SearchSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 25px;
  margin-bottom: 30px;
`;

const SearchTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #ffd700;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const SearchForm = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
`;

const SearchInput = styled.input`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 12px 16px;
  color: white;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #ffd700;
    background: rgba(255, 255, 255, 0.15);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const SearchSelect = styled.select`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 12px 16px;
  color: white;
  font-size: 1rem;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #ffd700;
  }
  
  option {
    background: #1e293b;
    color: white;
  }
`;

const SearchActions = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
`;

const SearchButton = styled.button`
  background: linear-gradient(45deg, #4caf50, #8bc34a);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 20px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 10px 25px rgba(76, 175, 80, 0.4);
  }
`;

const ClearButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 12px 24px;
  border-radius: 20px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }
`;

const GroupsSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 25px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 600;
  color: #ffd700;
  display: flex;
  align-items: center;
  gap: 15px;
`;

const RefreshButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 10px 20px;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }
`;

const GroupsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 20px;
`;

const GroupCard = styled.div<{ status: string }>`
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid ${props => {
    if (props.status === 'ACTIVELY_FORMING') return '#4caf50';
    if (props.status === 'IN_PROGRESS') return '#ff9800';
    if (props.status === 'COMPLETED') return '#9c27b0';
    return 'rgba(255, 255, 255, 0.2)';
  }};
  border-radius: 20px;
  padding: 25px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${props => {
      if (props.status === 'ACTIVELY_FORMING') return 'linear-gradient(90deg, #4caf50, #8bc34a)';
      if (props.status === 'IN_PROGRESS') return 'linear-gradient(90deg, #ff9800, #ffc107)';
      if (props.status === 'COMPLETED') return 'linear-gradient(90deg, #9c27b0, #e91e63)';
      return 'rgba(255, 255, 255, 0.2)';
    }};
  }
`;

const GroupHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
`;

const GroupAvatar = styled.div<{ ticketClass: string }>`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${props => {
    if (props.ticketClass === 'first') return 'linear-gradient(135deg, #ffd700, #ffed4e)';
    if (props.ticketClass === 'second') return 'linear-gradient(135deg, #c0c0c0, #e5e5e5)';
    return 'linear-gradient(135deg, #cd7f32, #daa520)';
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.2rem;
  color: ${props => props.ticketClass === 'second' ? '#333' : 'white'};
  border: 3px solid rgba(255, 255, 255, 0.3);
`;

const GroupInfo = styled.div`
  flex: 1;
`;

const GroupName = styled.h3`
  font-size: 1.4rem;
  font-weight: 600;
  color: white;
  margin: 0 0 8px 0;
`;

const GroupCategory = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 5px;
`;

const GroupSubtopics = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-bottom: 10px;
`;

const SubtopicTag = styled.span`
  background: rgba(255, 215, 0, 0.2);
  border: 1px solid rgba(255, 215, 0, 0.4);
  border-radius: 8px;
  padding: 2px 8px;
  color: #ffd700;
  font-size: 0.7rem;
  font-weight: 600;
`;

const GroupStats = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px;
  margin-bottom: 20px;
`;

const GroupStat = styled.div`
  text-align: center;
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
`;

const StatNumber = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  color: white;
  margin-bottom: 5px;
`;

const StatText = styled.div`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
`;

const GroupStatus = styled.div<{ status: string }>`
  position: absolute;
  top: 20px;
  right: 20px;
  padding: 6px 12px;
  border-radius: 15px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => {
    if (props.status === 'ACTIVELY_FORMING') return 'rgba(76, 175, 80, 0.2)';
    if (props.status === 'IN_PROGRESS') return 'rgba(255, 152, 0, 0.2)';
    if (props.status === 'COMPLETED') return 'rgba(156, 39, 176, 0.2)';
    return 'rgba(255, 255, 255, 0.1)';
  }};
  color: ${props => {
    if (props.status === 'ACTIVELY_FORMING') return '#4caf50';
    if (props.status === 'IN_PROGRESS') return '#ff9800';
    if (props.status === 'COMPLETED') return '#9c27b0';
    return 'rgba(255, 255, 255, 0.8)';
  }};
  border: 1px solid ${props => {
    if (props.status === 'ACTIVELY_FORMING') return '#4caf50';
    if (props.status === 'IN_PROGRESS') return '#ff9800';
    if (props.status === 'COMPLETED') return '#9c27b0';
    return 'rgba(255, 255, 255, 0.2)';
  }};
`;

const GroupActions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ variant: 'primary' | 'secondary' | 'success' }>`
  background: ${props => {
    if (props.variant === 'primary') return 'linear-gradient(45deg, #4caf50, #8bc34a)';
    if (props.variant === 'success') return 'linear-gradient(45deg, #ffd700, #ffed4e)';
    return 'rgba(255, 255, 255, 0.1)';
  }};
  color: ${props => props.variant === 'success' ? '#1e293b' : 'white'};
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 10px 20px;
  border-radius: 15px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  }
  
  &:disabled {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.5);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: rgba(255, 255, 255, 0.7);
`;

const EmptyIcon = styled.div`
  font-size: 4rem;
  margin-bottom: 20px;
  opacity: 0.5;
`;

const EmptyTitle = styled.h3`
  font-size: 1.5rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 10px;
`;

const EmptyText = styled.p`
  font-size: 1rem;
  line-height: 1.6;
`;

const GroupCompetitionDashboard: React.FC<GroupCompetitionDashboardProps> = ({
  currentUserId,
  onClose
}) => {
  const [showCreator, setShowCreator] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<GroupCompetition | null>(null);
  const [showLobby, setShowLobby] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [searchFilters, setSearchFilters] = useState<GroupSearchFilters>({});
  const [groups, setGroups] = useState<GroupCompetition[]>([]);
  const [filteredGroups, setFilteredGroups] = useState<GroupCompetition[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Sample data for demonstration
  useEffect(() => {
    const sampleGroups: GroupCompetition[] = [
      {
        id: '1',
        name: 'Math Gurus',
        description: 'Advanced mathematics competition for high school students',
        creatorId: 'user1',
        creatorName: 'John Doe',
        category: 'Mathematics',
        subcategory: 'Algebra',
        subtopics: ['Quadratic Equations', 'Linear Functions', 'Polynomials'],
        stakeAmount: 500,
        currentParticipants: 8,
        participants: [],
        status: 'ACTIVELY_FORMING',
        ticketClass: 'second',
        formationPeriod: { months: 0, weeks: 1, days: 0, hours: 0, minutes: 0, seconds: 0 },
        formationStartTime: new Date(),
        formationEndTime: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        totalPrizePool: 4000,
        appFeePercentage: 15,
        appFeeAmount: 600,
        rules: {
          maxWithdrawals: 3,
          gracePeriodMinutes: 5,
          tieBreaker: 'SPLIT_PRIZE',
          questionTimeLimit: 60,
          allowHints: true,
          allowCalculator: false,
          allowNotes: false,
          scoringSystem: {
            correctAnswer: 10,
            incorrectAnswer: 0,
            timeBonus: 2,
            difficultyMultiplier: 1.5,
            streakBonus: 5
          }
        },
        questions: [],
        createdAt: new Date(),
        updatedAt: new Date()
      },
      {
        id: '2',
        name: 'Science Squad',
        description: 'Physics and chemistry challenge',
        creatorId: 'user2',
        creatorName: 'Jane Smith',
        category: 'Science',
        subcategory: 'Physics',
        subtopics: ['Mechanics', 'Thermodynamics', 'Electromagnetism'],
        stakeAmount: 1000,
        currentParticipants: 12,
        participants: [],
        status: 'IN_PROGRESS',
        ticketClass: 'first',
        formationPeriod: { months: 0, weeks: 2, days: 0, hours: 0, minutes: 0, seconds: 0 },
        formationStartTime: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000),
        formationEndTime: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
        competitionStartTime: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
        totalPrizePool: 12000,
        appFeePercentage: 15,
        appFeeAmount: 1800,
        rules: {
          maxWithdrawals: 3,
          gracePeriodMinutes: 5,
          tieBreaker: 'SPLIT_PRIZE',
          questionTimeLimit: 90,
          allowHints: false,
          allowCalculator: true,
          allowNotes: false,
          scoringSystem: {
            correctAnswer: 15,
            incorrectAnswer: 0,
            timeBonus: 3,
            difficultyMultiplier: 2,
            streakBonus: 10
          }
        },
        questions: [],
        createdAt: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000),
        updatedAt: new Date()
      }
    ];

    setGroups(sampleGroups);
    setFilteredGroups(sampleGroups);
  }, []);

  const handleCreateGroup = (groupForm: GroupCompetitionForm) => {
    // In a real app, this would create the group via API
    console.log('Creating group:', groupForm);
    
    const newGroup: GroupCompetition = {
      id: Date.now().toString(),
      name: groupForm.name,
      description: groupForm.description,
      creatorId: currentUserId,
      creatorName: 'Current User',
      category: groupForm.category,
      subcategory: groupForm.subcategory,
      subtopics: groupForm.subtopics,
      stakeAmount: groupForm.stakeAmount,
      currentParticipants: 1,
      participants: [],
      status: 'ACTIVELY_FORMING',
      ticketClass: groupForm.ticketClass,
      formationPeriod: groupForm.formationPeriod,
      formationStartTime: new Date(),
      formationEndTime: new Date(Date.now() + 
        (groupForm.formationPeriod.months * 30 * 24 * 60 * 60 * 1000) +
        (groupForm.formationPeriod.weeks * 7 * 24 * 60 * 60 * 1000) +
        (groupForm.formationPeriod.days * 24 * 60 * 60 * 1000) +
        (groupForm.formationPeriod.hours * 60 * 60 * 1000) +
        (groupForm.formationPeriod.minutes * 60 * 1000) +
        (groupForm.formationPeriod.seconds * 1000)
      ),
      totalPrizePool: groupForm.stakeAmount,
      appFeePercentage: 15,
      appFeeAmount: groupForm.stakeAmount * 0.15,
      rules: groupForm.rules as any,
      questions: [],
      createdAt: new Date(),
      updatedAt: new Date()
    };

    setGroups(prev => [newGroup, ...prev]);
    setFilteredGroups(prev => [newGroup, ...prev]);
    setShowCreator(false);
  };

  const handleSearch = () => {
    let filtered = groups;

    if (searchFilters.category) {
      filtered = filtered.filter(g => g.category === searchFilters.category);
    }
    if (searchFilters.subcategory) {
      filtered = filtered.filter(g => g.subcategory === searchFilters.subcategory);
    }
    if (searchFilters.ticketClass) {
      filtered = filtered.filter(g => g.ticketClass === searchFilters.ticketClass);
    }
    if (searchFilters.status) {
      filtered = filtered.filter(g => g.status === searchFilters.status);
    }
    if (searchFilters.minStake) {
      filtered = filtered.filter(g => g.stakeAmount >= searchFilters.minStake!);
    }
    if (searchFilters.maxStake) {
      filtered = filtered.filter(g => g.stakeAmount <= searchFilters.maxStake!);
    }

    setFilteredGroups(filtered);
  };

  const handleClearFilters = () => {
    setSearchFilters({});
    setFilteredGroups(groups);
  };

  const handleJoinGroup = (groupId: string) => {
    const group = groups.find(g => g.id === groupId);
    if (group) {
      setSelectedGroup(group);
      setShowLobby(true);
    }
  };

  const handleViewGroup = (groupId: string) => {
    const group = groups.find(g => g.id === groupId);
    if (group) {
      setSelectedGroup(group);
      setShowLobby(true);
    }
  };

  const handleWithdraw = (groupId: string, reason: any, customReason?: string) => {
    console.log('Withdrawing from group:', groupId, reason, customReason);
    // In a real app, this would handle withdrawal via API
    setShowLobby(false);
  };

  const handleViewResults = (groupId: string) => {
    const group = groups.find(g => g.id === groupId);
    if (group) {
      setSelectedGroup(group);
      setShowResults(true);
    }
  };

  const handleViewDetails = (participantId: string) => {
    console.log('Viewing participant details:', participantId);
    // In a real app, this would show detailed participant information
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'ACTIVELY_FORMING':
        return <Users size={20} />;
      case 'IN_PROGRESS':
        return <Play size={20} />;
      case 'COMPLETED':
        return <Trophy size={20} />;
      default:
        return <Clock size={20} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVELY_FORMING':
        return 'linear-gradient(135deg, #4caf50, #8bc34a)';
      case 'IN_PROGRESS':
        return 'linear-gradient(135deg, #ff9800, #ffc107)';
      case 'COMPLETED':
        return 'linear-gradient(135deg, #9c27b0, #e91e63)';
      default:
        return 'rgba(255, 255, 255, 0.1)';
    }
  };

  return (
    <DashboardOverlay>
      <Header>
        <Title>
          <TitleIcon>🚢</TitleIcon>
          LAVERN TITAN - Group Competitions
        </Title>
        <HeaderActions>
          <CreateButton onClick={() => setShowCreator(true)}>
            <Plus size={20} />
            Create Group
          </CreateButton>
          <CloseButton onClick={onClose}>
            Close
          </CloseButton>
        </HeaderActions>
      </Header>

      <ContentArea>
        {/* Statistics Section */}
        <StatsSection>
          <StatCard>
            <StatIcon color="linear-gradient(135deg, #4caf50, #8bc34a)">
              <Users size={24} />
            </StatIcon>
            <StatValue>{groups.length}</StatValue>
            <StatLabel>Total Groups</StatLabel>
          </StatCard>
          
          <StatCard>
            <StatIcon color="linear-gradient(135deg, #ffd700, #ffed4e)">
              <Trophy size={24} />
            </StatIcon>
            <StatValue>
              {groups.filter(g => g.status === 'ACTIVELY_FORMING').length}
            </StatValue>
            <StatLabel>Forming</StatLabel>
          </StatCard>
          
          <StatCard>
            <StatIcon color="linear-gradient(135deg, #ff9800, #ffc107)">
              <Play size={24} />
            </StatIcon>
            <StatValue>
              {groups.filter(g => g.status === 'IN_PROGRESS').length}
            </StatValue>
            <StatLabel>In Progress</StatLabel>
          </StatCard>
          
          <StatCard>
            <StatIcon color="linear-gradient(135deg, #9c27b0, #e91e63)">
              <CheckCircle size={24} />
            </StatIcon>
            <StatValue>
              {groups.filter(g => g.status === 'COMPLETED').length}
            </StatValue>
            <StatLabel>Completed</StatLabel>
          </StatCard>
        </StatsSection>

        {/* Search Section */}
        <SearchSection>
          <SearchTitle>
            <Search size={24} />
            Find Group Competitions
          </SearchTitle>
          
          <SearchForm>
            <SearchInput
              placeholder="Search by name..."
              value={searchFilters.category || ''}
              onChange={(e) => setSearchFilters(prev => ({ ...prev, category: e.target.value }))}
            />
            
            <SearchSelect
              value={searchFilters.ticketClass || ''}
              onChange={(e) => setSearchFilters(prev => ({ ...prev, ticketClass: e.target.value as any }))}
            >
              <option value="">All Ticket Classes</option>
              <option value="third">Third Class</option>
              <option value="second">Second Class</option>
              <option value="first">First Class</option>
            </SearchSelect>
            
            <SearchSelect
              value={searchFilters.status || ''}
              onChange={(e) => setSearchFilters(prev => ({ ...prev, status: e.target.value as any }))}
            >
              <option value="">All Statuses</option>
              <option value="ACTIVELY_FORMING">Actively Forming</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="COMPLETED">Completed</option>
            </SearchSelect>
            
            <SearchInput
              type="number"
              placeholder="Min Stake (KES)"
              value={searchFilters.minStake || ''}
              onChange={(e) => setSearchFilters(prev => ({ ...prev, minStake: Number(e.target.value) }))}
            />
            
            <SearchInput
              type="number"
              placeholder="Max Stake (KES)"
              value={searchFilters.maxStake || ''}
              onChange={(e) => setSearchFilters(prev => ({ ...prev, maxStake: Number(e.target.value) }))}
            />
          </SearchForm>
          
          <SearchActions>
            <SearchButton onClick={handleSearch}>
              <Search size={16} />
              Search
            </SearchButton>
            <ClearButton onClick={handleClearFilters}>
              <Filter size={16} />
              Clear Filters
            </ClearButton>
          </SearchActions>
        </SearchSection>

        {/* Groups Section */}
        <GroupsSection>
          <SectionHeader>
            <SectionTitle>
              <BookOpen size={24} />
              Available Groups ({filteredGroups.length})
            </SectionTitle>
            <RefreshButton onClick={() => window.location.reload()}>
              <RefreshCw size={16} />
              Refresh
            </RefreshButton>
          </SectionHeader>
          
          {filteredGroups.length === 0 ? (
            <EmptyState>
              <EmptyIcon>🚢</EmptyIcon>
              <EmptyTitle>No Groups Found</EmptyTitle>
              <EmptyText>
                No group competitions match your current filters. 
                Try adjusting your search criteria or create a new group!
              </EmptyText>
            </EmptyState>
          ) : (
            <GroupsGrid>
              {filteredGroups.map((group) => (
                <GroupCard key={group.id} status={group.status}>
                  <GroupStatus status={group.status}>
                    {group.status.replace('_', ' ')}
                  </GroupStatus>
                  
                  <GroupHeader>
                    <GroupAvatar ticketClass={group.ticketClass}>
                      {group.name.charAt(0)}
                    </GroupAvatar>
                    <GroupInfo>
                      <GroupName>{group.name}</GroupName>
                      <GroupCategory>{group.category} • {group.subcategory}</GroupCategory>
                      <GroupSubtopics>
                        {group.subtopics.slice(0, 3).map((subtopic, index) => (
                          <SubtopicTag key={index}>{subtopic}</SubtopicTag>
                        ))}
                        {group.subtopics.length > 3 && (
                          <SubtopicTag>+{group.subtopics.length - 3} more</SubtopicTag>
                        )}
                      </GroupSubtopics>
                    </GroupInfo>
                  </GroupHeader>
                  
                  <GroupStats>
                    <GroupStat>
                      <StatNumber>{group.currentParticipants}</StatNumber>
                      <StatText>Participants</StatText>
                    </GroupStat>
                    <GroupStat>
                      <StatNumber>KES {group.stakeAmount.toLocaleString()}</StatNumber>
                      <StatText>Stake</StatText>
                    </GroupStat>
                  </GroupStats>
                  
                  <GroupActions>
                    {group.status === 'ACTIVELY_FORMING' && (
                      <ActionButton variant="primary" onClick={() => handleJoinGroup(group.id)}>
                        <Users size={16} />
                        Join Group
                      </ActionButton>
                    )}
                    
                    {group.status === 'IN_PROGRESS' && (
                      <ActionButton variant="success" onClick={() => handleViewGroup(group.id)}>
                        <Play size={16} />
                        View Progress
                      </ActionButton>
                    )}
                    
                    {group.status === 'COMPLETED' && (
                      <ActionButton variant="secondary" onClick={() => handleViewResults(group.id)}>
                        <Trophy size={16} />
                        View Results
                      </ActionButton>
                    )}
                    
                    <ActionButton variant="secondary" onClick={() => handleViewGroup(group.id)}>
                      <Eye size={16} />
                      View Details
                    </ActionButton>
                  </GroupActions>
                </GroupCard>
              ))}
            </GroupsGrid>
          )}
        </GroupsSection>
      </ContentArea>

      {/* Modals */}
      {showCreator && (
        <GroupCompetitionCreator
          onClose={() => setShowCreator(false)}
          onGroupCreated={handleCreateGroup}
        />
      )}
      
      {showLobby && selectedGroup && (
        <GroupCompetitionLobby
          group={selectedGroup}
          currentUserId={currentUserId}
          onClose={() => setShowLobby(false)}
          onJoinCompetition={handleJoinGroup}
          onWithdraw={handleWithdraw}
        />
      )}
      
      {showResults && selectedGroup && (
        <GroupCompetitionResults
          group={selectedGroup}
          results={{
            participants: [
              {
                userId: 'user1',
                userName: 'John Doe',
                score: 95,
                rank: 1,
                correctAnswers: 19,
                totalQuestions: 20,
                timeTaken: 1800,
                prizeAmount: 10200
              },
              {
                userId: 'user2',
                userName: 'Jane Smith',
                score: 90,
                rank: 2,
                correctAnswers: 18,
                totalQuestions: 20,
                timeTaken: 2100,
                prizeAmount: 0
              }
            ],
            winners: ['user1'],
            totalPrizePool: 10200,
            prizeDistribution: [
              {
                rank: 1,
                userId: 'user1',
                userName: 'John Doe',
                prizeAmount: 10200,
                percentage: 100
              }
            ],
            tieBreakerUsed: false,
            completedAt: new Date()
          }}
          onClose={() => setShowResults(false)}
          onViewDetails={handleViewDetails}
        />
      )}
    </DashboardOverlay>
  );
};

export default GroupCompetitionDashboard;
